<template>
    <div id="credit-card" class="content-spacer">
        <ViewHeader title="Credit Card Payment" />
        <div id="rest-of-body" class="drop-shadow-sides">
            <TotalDetail :isTipVisible="true" />
            <div class="view-content">
                <form id="paymentForm" ref="paymentForm" method="POST" action="">
                    <div class="field">
                        <label for="nameOnCard">Name on Card</label>
                        <input type="text" name="nameOnCard" id="nameOnCard" maxlength="64" v-model.trim="v$.nameOnCard.$model" :class="{ 'with-err-field': v$.nameOnCard.$error }" />
                        <div v-if="v$.nameOnCard.$error" class="error-message">
                            Enter Name on Card
                        </div>
                    </div>
                    <div class="field">
                        <label for="cardNumber">Credit Card Number</label>
                        <input type="text" name="cardNumber" id="cardNumber" maxlength="16" v-model.trim="v$.ccNumber.$model" @keypress="handleKeypress" @keyup="onCCNumberKeyUp" :class="{ 'with-err-field': v$.ccNumber.$error || hasCCMinLengthErr }" />
                        <div v-if="v$.ccNumber.$error" class="error-message">
                            Enter Credit Card Number
                        </div>
                        <div v-if="hasCCMinLengthErr" class="error-message">
                            Should be at least 4 characters
                        </div>
                    </div>
                    <div class="field">
                        <table>
                            <tr>
                                <th>Exp Month</th>
                                <th>Exp Year</th>
                                <th>Security Code</th>
                            </tr>
                            <tr>
                                <td>
                                    <select v-model="v$.expMonth.$model" :class="{ 'with-err-field': v$.expMonth.$error }" >
                                        <option value="" selected></option>
                                        <option value="01">01</option>
                                        <option value="02">02</option>
                                        <option value="03">03</option>
                                        <option value="04">04</option>
                                        <option value="05">05</option>
                                        <option value="06">06</option>
                                        <option value="07">07</option>
                                        <option value="08">08</option>
                                        <option value="09">09</option>
                                        <option value="10">10</option>
                                        <option value="11">11</option>
                                        <option value="12">12</option>
                                    </select>
                                </td>
                                <td>
                                    <select v-model="v$.expYear.$model" :class="{ 'with-err-field': v$.expYear.$error }" >
                                        <option value="" selected></option>
                                        <option v-for="(year, index) in selectYears" v-bind:key="index" v-bind:value="year">{{year}}</option>
                                    </select>
                                </td>
                                <td>
                                    <input type="password" autocomplete="off" name="cardCode" id="cardCode" v-model.trim="v$.securityCode.$model" maxlength="4" @keypress="handleKeypress" :class="{ 'with-err-field': v$.securityCode.$error }" />
                                </td>
                            </tr>
                            <tr v-if="hasExpOrSecurityCodeError">
                                <td>
                                    <div v-if="v$.expMonth.$error" class="error-message">
                                        Select Exp Month
                                    </div>
                                </td>
                                <td>
                                    <div v-if="v$.expYear.$error" class="error-message">
                                        Select Exp Year
                                    </div>
                                </td>
                                <td>
                                    <div v-if="v$.securityCode.$error" class="error-message">
                                        Enter Security Code
                                    </div>
                                </td>
                            </tr>
                        </table>
                    </div>
                    <div class="field" v-if="hasErrors">
                        <div v-for="(err, index) in errorMsgs" v-bind:key="index" class="error-message">{{err}}</div>
                    </div>
                    <button :disabled="!canSubmitPayment" class="button-primary">Pay</button>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
    import axios from "axios";
    import { mapState, mapGetters, mapMutations } from "vuex";
    import { appSettings } from "../mixins/app-settings";
    import { paymentMethods } from "../constants/payment-methods";
    import ViewHeader from "../components/ViewHeader";
    import TotalDetail from "../components/TotalDetail";
    import { useVuelidate } from '@vuelidate/core'
    import { required } from '@vuelidate/validators'

    export default {
        name: "CreditCardAuthorizeNet",
        components: {
            ViewHeader,
            TotalDetail
        },
        mixins: [appSettings],
        setup () {
            return {
                v$: useVuelidate()
            }
        },
        data(){
            return {
                apiLoginID: '',
                clientKey: '',
                scriptLoaded: false,
                isNumberValid: true,
                isExpirationValid: true,
                isCvvValid: true,
                nameOnCard: '',
                ccNumber: '',
                securityCode: '',
                selectYears: [],
                expMonth: '',
                expYear: '',
                errorMsgs: [],
                hasCCMinLengthErr: false,
                maskedCCNumber: ''
            }
        },
        validations () {
            return {
                nameOnCard: { required },
                ccNumber: { required },
                securityCode: { required },
                expMonth: { required },
                expYear: { required }
            }
        },
        computed: {
            ...mapState({
                siteId: state => state.session.siteId,
                accountId: state => state.session.accountId,
                checkId: state => state.session.checkId,
                tip: state => state.session.tip
            }),
            ...mapGetters([
                "balanceWithTip"
            ]),
            canSubmitPayment(){
                return this.nameOnCard !== "" && this.ccNumber !== "" && this.securityCode !== "" && this.expMonth !== "" && this.expYear !== "" && !this.hasCCMinLengthErr;
            },
            formattedYear(){
                return String(this.expYear).substring(2,4);
            },
            hasErrors(){
                return this.errorMsgs.length > 0;
            },
            hasExpOrSecurityCodeError(){
                return this.securityCode !== "" || this.expMonth !== "" || this.expYear !== "";
            }
        },
        methods:{
            ...mapMutations([
                "setPayment",
                "setLoadingStatus"
            ]),
            handleKeypress(){
                if (!/\d/.test(event.key)){
                    event.preventDefault();
                    return false;
                }
            },
            submitPayment() {
                this.setLoadingStatus(true);
                this.errorMsgs = [];
                this.hasCCMinLengthErr = false;
                const ccNumberLength = this.ccNumber.length;
                if(ccNumberLength >= 4){
                    const last4CCNumber = this.ccNumber.substring(ccNumberLength - 4);
                    this.maskedCCNumber = '*'.repeat(ccNumberLength - 4) + last4CCNumber;
                }
                else{
                    this.hasCCMinLengthErr = true;
                    this.setLoadingStatus(false);
                    return;
                }

                const authData = {
                    clientKey: this.clientKey,
                    apiLoginID: this.apiLoginID
                };

                const cardData = {
                    cardNumber: this.ccNumber,
                    month: this.expMonth,
                    year: this.formattedYear,
                    cardCode: this.securityCode,
                    fullName: this.nameOnCard
                };

                const secureData = {
                    authData: authData,
                    cardData: cardData
                };

                try{
                    /* eslint-disable */
                    Accept.dispatchData(secureData, this.handleResponse);
                    /* eslint-enable */
                }
                catch{
                    console.error('Unable to process payment.');
                }
                finally{
                    this.setLoadingStatus(false);
                }
            },
            handleResponse(response){
                if (response.messages.resultCode === "Error") {
                    let i = 0;
                    while (i < response.messages.message.length) {
                        const errMsg = response.messages.message[i].text;
                        this.errorMsgs.push(errMsg);
                        i = i + 1;
                    }
                } else {
                    this.paymentFormUpdate(response.opaqueData);
                }
            },
            paymentFormUpdate(opaqueData){
                this.ccNumber = "";
                this.nameOnCard = "";
                this.securityCode = "";
                this.expMonth = "";
                this.expYear = "";
                this.processPayment(opaqueData.dataDescriptor, opaqueData.dataValue);
            },
            async processPayment(dataDescriptor, dataValue){
                this.setLoadingStatus(true);
                let request = {
                    siteId: this.siteId,
                    accountId: this.accountId,
                    checkId: this.checkId,
                    paymentAmount: this.balanceWithTip.toFixed(2),
                    tipAmount: this.tip.toFixed(2),
                    paymentNonce: dataValue,
                    descriptor: dataDescriptor,
                    maskedCreditCardNumber: this.maskedCCNumber
                };

                await axios.post(this.getApiBaseUrl() + "/payment/payment", request)
                    .then(response => {
                        if(response.data){
                            let payment = response.data;
                            payment.paymentName = paymentMethods.creditCard;
                            this.setPayment(payment);
                            this.$router.push({ name: "PaymentSummary" });
                        }
                    })
                    .catch(() => {
                        this.setLoadingStatus(false);
                        this.$router.push({ name: "Error", params: { errorCode: "1024" } });
                    }).finally(() =>{
                        this.setLoadingStatus(false);
                    });
            },
            async loadScriptAndSetupView() {
                this.setLoadingStatus(true);

                const vm = this;
                await axios.get(this.getApiBaseUrl() + "/authorizenet/getauthorizenetapiurl/" + this.siteId + "/" + this.accountId)
                    .then(response => {
                        if (response.data) {
                            const script = document.createElement('script');
                            script.src = response.data.apiUrl;

                            script.onload = async () => {
                                await vm.setupView();
                                vm.setLoadingStatus(false);
                            };

                            script.onerror = () => {
                                console.error('Authorize.net script not loaded');
                                vm.setLoadingStatus(false);
                            };

                            document.head.appendChild(script);
                        }
                    })
                    .catch(() => {
                        console.error('Error getting authorize.net api url.');
                        this.$router.push({ name: "Error", params: { errorCode: "1024" } });
                    });
            },
            setupSelectYear(){
                const currentYear = new Date().getFullYear();
                const endYear = currentYear + 10; // 10 year range
                for(let i = currentYear; i <= endYear; i++){
                    this.selectYears.push(i);
                }
            },
            async setupView(){
                this.setLoadingStatus(true);

                this.setupSelectYear();
                this.$refs["paymentForm"].addEventListener("submit", (event) => {
                    event.preventDefault();
                    this.submitPayment();
                });

                const vm = this;
                await axios.get(this.getApiBaseUrl() + "/authorizenet/getmerchantdetails/" + this.siteId + "/" + this.accountId)
                    .then(response => {
                        if(response.data){
                            vm.apiLoginID = response.data.apiLoginId;
                            vm.clientKey = response.data.clientKey;
                        }
                    })
                    .catch(() => {
                        this.$router.push({ name: "Error", params: { errorCode: "1024" } });
                    });
            },
            onCCNumberKeyUp(){
                this.hasCCMinLengthErr = false;
            }
        },
        async mounted() {
            await this.loadScriptAndSetupView();
        }
    }
</script>

<style scoped>
    #credit-card {
        display: flex;
        flex-direction: column;
    }

    #rest-of-body {
        flex-grow: 1;
        background-color: white;
        padding-bottom: 60px;
    }

    .field {
        text-align: left;
        margin-bottom: 15px;
    }

    input, select {
        width: 100%;
        height: 44px;
        font-size: 16px;
        box-sizing: border-box;
        color: #495057;
        border: none;
        border-bottom: 1px solid black; 
    }

    td {
        width: 25%;
        padding-right: 10px;
    }

    td:last-of-type{
        padding-right: 0;
    }

    div.inline {
        width: 49%;
    }

        div.inline:last-child {
            float: right;
        }

    .spacer {
        width: 10px;
    }

    button {
        width: 100%;
        margin-top: 15px;
    }

    .with-err-field {
        border-color: red;
    }

</style>